import { Component, OnInit } from '@angular/core';
import { take, catchError } from 'rxjs/operators';
import moment from 'moment';
import { WarehousingDashboard, StockException, RecentMovement } from '../../models/warehousing-dashboard.model';
import { WarehousingDashboardService } from '../../services/warehousing-dashboard.service';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { StockWeightService } from '../../services/stock-weight.service';

/** Rows shown in each exception callout before it offers to show the rest. */
const EXCEPTION_PREVIEW: number = 10;

@Component({
    selector: 'app-warehousing-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent implements OnInit {

  dashboard: WarehousingDashboard = new WarehousingDashboard();

  loading: boolean = true;

  /** Set when the load failed, so the screen says so instead of showing zeroes as fact. */
  loadFailed: boolean = false;

  showAllNegative: boolean = false;
  showAllZero: boolean = false;

  constructor(private warehousingDashboardService: WarehousingDashboardService,
    private stockQuantityService: StockQuantityService,
    private stockWeightService: StockWeightService) { }

  ngOnInit(): void {
    this.loadDashboard();
  }

  loadDashboard() {
    this.loading = true;
    this.loadFailed = false;

    this.warehousingDashboardService.getDashboard()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      this.loading = false;
      this.loadFailed = true;
      return e;
    }))
    .subscribe((data: WarehousingDashboard) => {
      this.dashboard = data;
      this.loading = false;
    })
  }

  /**
   * Negative stock is the headline of this screen, so the callout is never collapsed by
   * default and the count is always shown.
   */
  get negativeCount(): number {
    return this.dashboard.negative.length;
  }

  get zeroCount(): number {
    return this.dashboard.zero.length;
  }

  get visibleNegative(): StockException[] {
    return this.showAllNegative
      ? this.dashboard.negative
      : this.dashboard.negative.slice(0, EXCEPTION_PREVIEW);
  }

  get visibleZero(): StockException[] {
    return this.showAllZero
      ? this.dashboard.zero
      : this.dashboard.zero.slice(0, EXCEPTION_PREVIEW);
  }

  get exceptionPreview(): number {
    return EXCEPTION_PREVIEW;
  }

  /** qty is a float, so this goes through the shared epsilon rather than testing a sign. */
  isNegative(qty: number): boolean {
    return this.stockQuantityService.isNegative(qty);
  }

  /**
   * Renders a quantity without hiding real precision — the stock service rounds to the
   * places the backend stores rather than to two.
   */
  formatQty(qty: number): string {
    return this.stockQuantityService.round(qty).toString();
  }

  formatDateTime(value: string): string {
    return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '';
  }

  /**
   * A scale reading, blank where the goods were not weighed. Deliberately not `formatQty`: that
   * answers 0 for a null, which would state a weighing that never happened.
   */
  formatWeight(weight: number): string {
    return this.stockWeightService.format(weight);
  }

  /** Net moved, blank unless the movement was weighed at both ends. */
  formatNetWeight(movement: RecentMovement): string {
    return this.stockWeightService.formatNet(movement);
  }

  /**
   * Money is rounded once, here at the boundary, and only for display.
   *
   * The sign goes outside the symbol: `£-1000.00` was what the naive concatenation produced,
   * and it first appeared the moment the dashboard began reporting negative value at all.
   * On a screen whose whole purpose is making a write-down legible, money that reads as a
   * typo is money the reader discounts.
   */
  formatMoney(value: number): string {
    if (value === null || value === undefined) {
      return '—';
    }

    const amount = Number(value);

    return (amount < 0 ? '-£' : '£') + Math.abs(amount).toFixed(2);
  }

  /**
   * True when the valuation covers less than the whole warehouse.
   *
   * `cost` is nullable: NULL means never priced, 0 means deliberately priced at zero for 3PL
   * stock that belongs to a customer. So a total omits every unit nobody has priced, and the
   * coverage figures must appear beside the money rather than being inferable from a zero.
   */
  get valuationIncomplete(): boolean {
    return this.valuationCoverageIncomplete || this.valuationHasNegativeValue;
  }

  /**
   * True when the total leaves stock out. Distinct from `valuationIncomplete`, which is also
   * true when the money is wrong — the two need separate wording, so they need separate tests.
   * Without the split, a warehouse whose only problem was negative value rendered the "covers
   * part of the warehouse" heading above an empty list.
   */
  get valuationCoverageIncomplete(): boolean {
    const v = this.dashboard.summary.valuation;

    return v.unitsWithoutCostBasis > 0
      || v.productsWithoutCostBasis > 0
      || v.unitsFromUncostedMovements > 0;
  }

  /**
   * True when some of the money is negative, which is always an error rather than a fact.
   *
   * Separated from the rest of `valuationIncomplete` because it is a different claim: the other
   * terms say the total omits stock, this one says the total is wrong about the stock it did
   * include. Two products each holding 100 units at £10, one written down by £2,000, reported
   * "£0.00 across 200 unit(s) of 200 on hand" and then reassured the reader the figure covered
   * the whole warehouse. £1,000 of real stock, described as worthless, with the assurance
   * attached. Every one of the old terms was zero, which is why they could not catch it.
   *
   * `negativeValueProducts` is included even though it is measured over the whole ledger rather
   * than the holdings shown: if the costing found negative money anywhere, this figure has not
   * earned the word "complete".
   */
  get valuationHasNegativeValue(): boolean {
    const v = this.dashboard.summary.valuation;

    return v.unitsAtNegativeCost > 0
      || v.productsAtNegativeCost > 0
      || v.negativeValueProducts > 0
      || v.negativeUnits > 0;
  }

  /**
   * True when a revaluation was posted against a product with nothing to divide it into.
   *
   * Its own claim, and deliberately not folded into either of the two above. The coverage terms say
   * the total leaves stock out; the negative terms say the total is wrong about stock it included.
   * This says money was entered that never landed anywhere - the product had no costed intake, so
   * there was no denominator, and the amount stayed where it was rather than moving a unit cost.
   * Folding it into either would put it under a heading that does not describe it, and it would
   * only ever be seen when one of those was independently true.
   *
   * The backend has always computed this (`product-stock-costing.ts:225`) and published it through
   * the valuation, and it has always been on this model. No template has ever rendered it, so an
   * operator revaluing a product with no intake saw the figure accepted and simply vanish. That is
   * the one case where saying nothing is worse than saying the number is zero.
   *
   * Tested against the shared epsilon rather than `!== 0`, in line with the rest of the module: it
   * is a sum of signed adjustments, so a run of them can leave float dust either side of zero.
   *
   * That epsilon lives on `StockQuantityService`, a quantity primitive, and this is money - the
   * crossover §6 of the status doc already records as deferred. Reusing it is the lesser evil while
   * both round to four places; inventing a second epsilon here would mean two answers to what zero
   * is, which is a worse problem than the one being deferred.
   */
  get valuationHasStrandedValue(): boolean {
    return !this.stockQuantityService.isZero(
      this.dashboard.summary.valuation.strandedValueAdjustment);
  }

  /** True when nothing at all has been priced, which makes the figure meaningless rather than partial. */
  get valuationCoversNothing(): boolean {
    return this.dashboard.summary.valuation.valuedUnits === 0
      && this.dashboard.summary.valuation.unitsWithoutCostBasis > 0;
  }

  /** Non-zero means the stored levels and the ledger disagree — a defect, not noise. */
  get reconciliationDiscrepancies(): number {
    return this.dashboard.summary.reconciliationDiscrepancies;
  }
}
